<form *ngIf="form" [formGroup]="form">
    <ph-field>
        <ph-label required>Bank account name</ph-label>
        <ph-input formControlName="friendlyName" ngDefaultControl name="account-name" placeholder="e.g. Operating Account, Savings, Money Market"></ph-input>
        <lf-control-messages hint [control]="form.controls['friendlyName']"></lf-control-messages>
    </ph-field>

    <ng-container *ngIf="!treasuryRestricted">
        <ph-field *ngIf="updateAccount?.depositBankAccount && jwt.superUser">
            <ph-hint has-error>
                This account is already connected to Stripe. Use caution if updating banking details.
                <app-super-star></app-super-star>
            </ph-hint>
        </ph-field>

        <ph-field>
            <ph-label>Bank Routing Number</ph-label>
            <ph-input formControlName="routingNumber" ngDefaultControl name="routing-number"></ph-input>
            <ph-hint *ngIf="updateAccount?.depositBankAccount && !jwt.superUser">Account and routing numbers cannot be updated after enabling deposits.</ph-hint>
            <lf-control-messages hint [control]="form.controls['routingNumber']"></lf-control-messages>
        </ph-field>

        <ph-field *ngIf="!updateAccount?.depositBankAccount || jwt.superUser">
            <ph-label>Bank Account Number</ph-label>
            <ph-input formControlName="accountNumber" ngDefaultControl name="account-number"></ph-input>
            <ph-hint *ngIf="updateAccount?.last4">Current account number hidden for security. Leave this field blank to keep the old value.</ph-hint>
            <lf-control-messages hint [control]="form.controls['accountNumber']"></lf-control-messages>
        </ph-field>

        <ph-field inline>
            <ph-switch formControlName="useForDeposits" ngDefaultControl aria-label="Toggle deposit account"></ph-switch>
            <ph-label (click)="toggleControl('useForDeposits')">Save as a deposit destination for online payments from homeowners.</ph-label>
        </ph-field>

        <ng-container *ngIf="!form.get('useForDeposits').value && updateAccount?.depositBankAccount">
            <ph-field *ngIf="replacementCandidates.length > 0">
                <ph-label>Select a replacement deposit account for outstanding charges</ph-label>
                <ph-select formControlName="replacementDepositAccount"
                           ngDefaultControl
                           placeholder="Select Replacement Account"
                           aria-label="Select Replacement Account"
                           [items]="replacementItems"
                           [hasError]="showError('replacementDepositAccount')"></ph-select>
                <lf-control-messages hint [control]="form.controls['replacementDepositAccount']"></lf-control-messages>
            </ph-field>
            <ph-field *ngIf="replacementCandidates.length === 0">
                <ph-alert status="error">
                    <ph-stack gap="tight">
                        <span>This is your last deposit account. You must have at least one deposit account on file to make use of the site's features.</span>
                        <span>Removing your last deposit account would leave nowhere for your users' payments to go. If you wish to cancel your account with us, please contact our Support Team. Otherwise, please add a new deposit account before returning to remove this one.</span>
                    </ph-stack>
                </ph-alert>
            </ph-field>
        </ng-container>

        <ng-container *ngIf="updateAccount?.isAllianceAccount || form.get('routingNumber').value === '122105980'; else plaidFeed">
            <ph-field inline>
                <ph-switch formControlName="allianceTransactionImportEnabled" ngDefaultControl aria-label="Import transactions from your bank"></ph-switch>
                <ph-label (click)="toggleControl('allianceTransactionImportEnabled')">Import transactions from Alliance Bank's daily feed.</ph-label>
            </ph-field>
            <ph-field *ngIf="form.get('allianceTransactionImportEnabled').value">
                <ph-label>Import transactions after</ph-label>
                <ph-date-input formControlName="allianceTransactionImportAfter" ngDefaultControl placeholder="Start date..."></ph-date-input>
            </ph-field>
        </ng-container>

        <ng-template #plaidFeed>
            <ph-field inline>
                <ph-switch formControlName="connectToTransactionFeed" ngDefaultControl aria-label="Import transactions from your bank"></ph-switch>
                <ph-label (click)="toggleControl('connectToTransactionFeed')">Import transactions from your bank.</ph-label>
            </ph-field>

            <ng-container *ngIf="form.get('connectToTransactionFeed').value">
                <ng-container *ngIf="allTokens.length">
                    <ph-field>
                        <ph-label>Select Bank Login</ph-label>
                        <ph-select #tokenSelect
                                   placeholder="Select an Existing Login"
                                   aria-label="Select Bank Login"
                                   [items]="tokenItems"
                                   [value]="selectedTokenId"
                                   [hasError]="showError('plaidToken')"
                                   (ph-change)="onTokenSelected($event.detail)"></ph-select>
                    </ph-field>

                    <ng-container *ngIf="form.get('plaidToken').value">
                        <ph-field *ngIf="!form.get('plaidToken').value.plaidError; else tokenError">
                            <ph-label>Select Account</ph-label>
                            <ph-select #accountSelect
                                       placeholder="Select an Existing Account"
                                       aria-label="Select Account"
                                       [items]="accountItems"
                                       [value]="selectedAccountId"
                                       [hasError]="showError('plaidAccount')"
                                       (ph-change)="onAccountSelected($event.detail)"></ph-select>
                        </ph-field>

                        <ng-template #tokenError>
                            <ph-field>
                                <ph-hint has-error>
                                    This login requires attention. Please click
                                    <ph-link-button variant="primary" (click)="updateToken(form.get('plaidToken').value)">here</ph-link-button>
                                    to repair the connection.
                                </ph-hint>
                            </ph-field>
                        </ng-template>
                    </ng-container>
                </ng-container>

                <ph-field>
                    <ph-label>Import transactions after</ph-label>
                    <ph-date-input formControlName="plaidImportAfter" ngDefaultControl placeholder="Start date..."></ph-date-input>
                </ph-field>
            </ng-container>
        </ng-template>
    </ng-container>
</form>

<app-confirm-with-selection-dialog [open]="connectionPickerOpen"
                                   [data]="connectionPickerData"
                                   (closed)="onConnectionPickerClosed($event)"></app-confirm-with-selection-dialog>

<app-plaid-search-dialog [open]="plaidSearchOpen"
                         product="transactions"
                         (closed)="onPlaidSearchClosed($event)"></app-plaid-search-dialog>

<app-existing-bank-connection-dialog [open]="existingConnectionOpen"
                                     [bankName]="existingConnectionBankName"
                                     (closed)="onExistingConnectionClosed($event)"></app-existing-bank-connection-dialog>
